import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);

  const loadCompanies = useCallback(() => {
    if (!localStorage.getItem("omnia_token")) return;
    api
      .get("/people/companies")
      .then((r) => {
        setCompanies(r.data.companies || []);
        setActiveCompany(r.data.active_company_id || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("omnia_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadCompanies();
      })
      .catch(() => {
        localStorage.removeItem("omnia_token");
      })
      .finally(() => setLoading(false));
  }, [loadCompanies]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Login en dos pasos: la contraseña devuelve un pending token; el MFA emite el
    // access token final. Devolvemos el sobre para que la pantalla de login siga el flujo.
    if (data.mfa_required) return { mfa_required: true, mfa_enrolled: data.mfa_enrolled, has_passkeys: data.has_passkeys, pending_token: data.pending_token };
    finishLogin(data);
    return data.user;
  };

  const finishLogin = (data) => {
    localStorage.setItem("omnia_token", data.access_token);
    localStorage.removeItem("omnia_acting_tenant");
    setUser(data.user);
    loadCompanies();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const registerCompany = async (payload) => {
    const { data } = await api.post("/auth/register-company", payload);
    return data;
  };

  // G-004 / 2.3 — switch the active company from any screen.
  const switchCompany = async (companyId) => {
    await api.post("/people/companies/switch", { company_id: companyId });
    const isDefault = companies.find((c) => c.id === companyId)?.is_default;
    if (isDefault) localStorage.removeItem("omnia_acting_tenant");
    else localStorage.setItem("omnia_acting_tenant", companyId);
    window.location.reload();
  };

  const logout = () => {
    localStorage.removeItem("omnia_token");
    localStorage.removeItem("omnia_acting_tenant");
    setUser(null);
    setCompanies([]);
    setActiveCompany(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, companies, activeCompany, login, finishLogin, register, registerCompany, switchCompany, reloadCompanies: loadCompanies, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
